import { BadgeTypeEnum } from '@getvim/atomic-ui';
import phoneFormatter from 'phone-formatter';
import {
  CostEstimation,
  FindResult,
  GetResultsOptions,
  Location,
  Performance,
  ProviderType,
} from '../../../../types/find';
import {
  FacilityResultSpecificFields,
  ProviderResultSpecificFields,
} from '../../../../types/find/uhc';
import {
  AddressWithDistanceAndPhone,
  BadgeDetails,
  ComplexName,
  CostDetail,
  OrderAssistOption,
  OrderAssistOptionType,
} from '../../../../types';
import { formatOpeningHours } from './formatOpeningHours';
import { getOrderAssistUIString } from '../../../../consts/strings';
import { getGarnerSpecialtyById } from '../../../../consts/teladoc-garner/garnerSpecialties';

const getCostDetail = (costEstimation: CostEstimation | undefined): CostDetail | undefined => {
  if (!costEstimation) {
    return undefined;
  }
  const { min, max, description } = costEstimation;
  const isSameCost = min === max;
  const costText = isSameCost ? `$${min}` : `$${min} - $${max}`;
  return {
    cost: costText,
    message: description ?? '',
  };
};

const getBadges = (performances: Performance[]): BadgeDetails[] => {
  return performances.map((performance) => {
    const { code, description } = performance;
    return {
      type: code as BadgeTypeEnum,
      description,
      title: '',
    };
  });
};

const getLocations = (locations: Location[]): AddressWithDistanceAndPhone[] => {
  return locations.map((location) => {
    const {
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      distance,
      phoneNumber,
      fax,
      officeName,
      acceptNewPatients,
      implementationSpecificFields,
      openingHours,
    } = location;

    const formattedPhone =
      phoneNumber?.number && phoneNumber?.number !== 'N/A'
        ? phoneFormatter.format(phoneNumber.number, '(NNN) NNN-NNNN')
        : getOrderAssistUIString('phone_number_not_available_label');

    const formattedFax =
      fax?.number && fax?.number !== 'N/A'
        ? phoneFormatter.format(fax.number, '(NNN) NNN-NNNN')
        : undefined;

    const formattedOpeningHours = formatOpeningHours(openingHours);

    return {
      address1: addressLine1,
      address2: addressLine2,
      officeName,
      city,
      state,
      zipCode: zip,
      distance: distance ?? '',
      acceptNewPatients,
      phone: formattedPhone,
      fax: formattedFax,
      implementationSpecificFields,
      openingHours: formattedOpeningHours,
    };
  });
};

const getComplexName = (findResult: FindResult): ComplexName | undefined => {
  const { firstName, lastName } = findResult;

  return {
    firstName: firstName as string,
    lastName: lastName as string,
  };
};

export const getResults = (options: GetResultsOptions): OrderAssistOption[] => {
  const { results, rankStarter } = options;
  return results.map((findResult: FindResult, index: number) => {
    const {
      providerType,
      npi,
      name,
      locations: locationsRaw,
      specialties: specialtiesRaw,
      preferredProvider,
      implementationSpecificFields,
      teladocGarner,
    } = findResult;
    const isProvider = providerType === ProviderType.PROVIDER;
    const resultSpecificFields = implementationSpecificFields as ProviderResultSpecificFields &
      FacilityResultSpecificFields;
    const {
      tin,
      mpin,
      tier,
      tierType,
      providerGroupIndicator,
      organizationName,
      acceptingNewPatients,
      organizationTypeDescription,
      preferredLab,
      freeStandingFacility,
      premiumDesignationLong,
      premiumDesignationText,
      plnDdpCd,
    } = resultSpecificFields || {};

    const firstLocation: Location | undefined = locationsRaw?.[0];
    const { performance, costEstimation, highValuePerformance } = firstLocation || {};

    const specialties = teladocGarner?.garnerSpecialty
      ? [
          {
            id: teladocGarner.garnerSpecialty,
            description: getGarnerSpecialtyById(teladocGarner.garnerSpecialty)?.label ?? '',
          },
        ]
      : specialtiesRaw;

    const locationsCommon = getLocations(locationsRaw ?? []);
    const locations = teladocGarner?.distanceMi
      ? locationsCommon.map((location) => {
          return {
            ...location,
            distance: teladocGarner.distanceMi?.toString() ?? '',
          };
        })
      : locationsCommon;

    return {
      type: isProvider ? OrderAssistOptionType.Provider : OrderAssistOptionType.Facility,
      details: {
        complexName: getComplexName(findResult),
        name,
        npi,
        tin,
        mpin,
        preferredProvider,
        acoName: organizationName,
        specialties,
        highValuePerformance,
      },
      locations,
      badges: getBadges(performance ?? []),
      costDetail: getCostDetail(costEstimation),
      extraInfo: {
        tier,
        tierType,
        providerGroupIndicator,
        ...(isProvider
          ? {
              acceptingNewPatients,
              premiumDesignationText,
              premiumDesignationLong,
            }
          : {
              organizationTypeDescription,
              preferredLab,
              freeStandingFacility,
              plnDdpCd,
            }),
      },
      ...(teladocGarner
        ? {
            teladocGarnerExtraInfo: {
              score: teladocGarner.overallScore,
              rating: teladocGarner.reviewStars
                ? Math.round(teladocGarner.reviewStars * 100) / 100
                : undefined,
              networkStatus: teladocGarner.networkStatus,
              garnerSpecialty: teladocGarner.garnerSpecialty,
            },
          }
        : {}),
      rank: (rankStarter ?? 0) + index + 1,
    };
  });
};
